import { useEffect } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  currentNewRequisition,
  currentNewRequisitionEdit,
} from "../../recoil/atoms";
import { NewRequisitionForm } from "../../recoil/types";
import ShipKitToPatientForm, {
  ShipKitShippingInfo,
} from "./ShipKitToPatientForm";
import { useMirrorRequisitionToCreateOrderState } from "./useMirrorRequisitionToCreateOrderState";

const NewReqShippingInformation: React.FC<{
  preflight: boolean;
  submittedRequisition?: NewRequisitionForm;
}> = ({ preflight = false, submittedRequisition }) => {
  const newGlobalRequisition = useRecoilValue(currentNewRequisition);
  const [newRequisition, setNewRequisition] = useRecoilState(
    currentNewRequisitionEdit,
  );
  const { requisitionForm } = newRequisition;

  useEffect(() => {
    const source = submittedRequisition || newGlobalRequisition;
    setNewRequisition({
      preflight,
      requisitionForm: source.shippingAddressTouched
        ? source
        : {
            ...source,
            shippingFirstName: source.firstName,
            shippingLastName: source.lastName,
            shippingFullAddress: source.fullAddress,
            shippingStreetAddress: source.streetAddress,
            shippingAptUnit: source.aptUnit,
            shippingCity: source.city,
            shippingState: source.state,
            shippingZipCode: source.zipCode,
          },
    });
  }, [preflight]);

  useMirrorRequisitionToCreateOrderState({
    enabled: !preflight,
    requisitionForm,
  });

  const patientAddress: ShipKitShippingInfo = {
    firstName: requisitionForm.firstName,
    lastName: requisitionForm.lastName,
    fullAddress: requisitionForm.fullAddress,
    streetAddress: requisitionForm.streetAddress,
    aptUnit: requisitionForm.aptUnit,
    city: requisitionForm.city,
    state: requisitionForm.state,
    zipCode: requisitionForm.zipCode,
  };

  const shippingInfo: ShipKitShippingInfo = {
    firstName: requisitionForm.shippingFirstName,
    lastName: requisitionForm.shippingLastName,
    fullAddress: requisitionForm.shippingFullAddress,
    streetAddress: requisitionForm.shippingStreetAddress,
    aptUnit: requisitionForm.shippingAptUnit,
    city: requisitionForm.shippingCity,
    state: requisitionForm.shippingState,
    zipCode: requisitionForm.shippingZipCode,
  };

  const setShippingInfo = (info: ShipKitShippingInfo) =>
    setNewRequisition((prev) => ({
      ...prev,
      requisitionForm: {
        ...prev.requisitionForm,
        shippingAddressTouched: true,
        shippingFirstName: info.firstName,
        shippingLastName: info.lastName,
        shippingFullAddress: info.fullAddress,
        shippingStreetAddress: info.streetAddress,
        shippingAptUnit: info.aptUnit,
        shippingCity: info.city,
        shippingState: info.state,
        shippingZipCode: info.zipCode,
      },
    }));

  const onShipToPatientChange = (checked: boolean) =>
    setNewRequisition((prev) => ({
      ...prev,
      requisitionForm: { ...prev.requisitionForm, isShipToPatient: checked },
    }));

  const onSameAsPatientToggle = (checked: boolean) =>
    setNewRequisition((prev) => ({
      ...prev,
      requisitionForm: {
        ...prev.requisitionForm,
        sameAsPatientShippingAddress: checked,
      },
    }));

  return (
    <ShipKitToPatientForm
      mode="order"
      shipToPatient={requisitionForm.isShipToPatient}
      onShipToPatientChange={onShipToPatientChange}
      sameAsPatientAddress={requisitionForm.sameAsPatientShippingAddress}
      onSameAsPatientToggle={onSameAsPatientToggle}
      shippingInfo={shippingInfo}
      onShippingInfoChange={setShippingInfo}
      patientAddress={patientAddress}
      disabled={preflight}
    />
  );
};

export default NewReqShippingInformation;
